import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Building2, CheckCircle2, XCircle, Clock, Send, FileText, Download,
  Loader2, MessageSquare, Mail, RefreshCw, Search,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATUSES = ['all', 'submitted', 'pending_review', 'approved', 'rejected', 'suspended'];

export default function CompanyApplications() {
  const { token } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [actionType, setActionType] = useState(null);   // approve | reject | info | resend
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = async () => {
    setLoading(true);
    try {
      const url = `${API_URL}/api/company-onboarding/applications${statusFilter !== 'all' ? `?status_filter=${statusFilter}` : ''}`;
      const res = await fetch(url, { headers });
      const data = await res.json();
      if (res.ok) setApps(data.applications || []);
      else toast.error(data.detail || 'Failed to load');
    } catch { toast.error('Network error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const openDetail = async (app) => {
    setSelected(app); setDetail(null);
    try {
      const res = await fetch(`${API_URL}/api/company-onboarding/applications/${app.application_id}`, { headers });
      const data = await res.json();
      if (res.ok) setDetail(data);
    } catch { toast.error('Failed to load application'); }
  };

  const downloadLoi = async (fileId, filename) => {
    try {
      const res = await fetch(`${API_URL}/api/company-onboarding/loi/${fileId}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) { toast.error('Download failed'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const submitAction = async () => {
    if (!selected || !actionType) return;
    if ((actionType === 'reject' || actionType === 'info') && !note.trim()) {
      toast.error('Please provide a reason / note'); return;
    }
    setBusy(true);
    const endpoint = {
      approve: 'approve', reject: 'reject', info: 'request-info', resend: 'resend-activation',
    }[actionType];
    try {
      const body = actionType === 'resend' ? {} : { note };
      const res = await fetch(`${API_URL}/api/company-onboarding/applications/${selected.application_id}/${endpoint}`, {
        method: 'POST', headers, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok) {
        const messages = {
          approve: 'Application approved — activation email sent',
          reject: 'Application rejected',
          info: 'Information request sent',
          resend: 'Activation email re-sent',
        };
        toast.success(messages[actionType]);
        setActionType(null); setNote(''); setSelected(null);
        load();
      } else toast.error(data.detail || 'Action failed');
    } catch { toast.error('Network error'); }
    finally { setBusy(false); }
  };

  const filtered = apps.filter((a) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (a.company_name || '').toLowerCase().includes(q) ||
      (a.primary_contact_email || '').toLowerCase().includes(q) ||
      (a.company_email || '').toLowerCase().includes(q) ||
      (a.application_id || '').toLowerCase().includes(q)
    );
  });

  const statusBadge = (s) => {
    const map = {
      submitted: 'bg-sky-500/20 text-sky-300 border-sky-500/30',
      pending_review: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
      approved: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
      rejected: 'bg-red-500/20 text-red-300 border-red-500/30',
      suspended: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
    };
    return <Badge className={map[s] || map.submitted}>{s}</Badge>;
  };

  return (
    <div className="space-y-4" data-testid="company-applications-panel">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex gap-3 items-center">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44 bg-slate-800 border-slate-700 text-slate-200" data-testid="status-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search company or email…" className="pl-9 w-64 bg-slate-800 border-slate-700 text-slate-200" data-testid="search-applications-input" />
          </div>
        </div>
        <Button variant="outline" onClick={load} className="border-slate-700 text-slate-200 hover:bg-slate-800" data-testid="refresh-apps-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="flex items-center justify-between bg-slate-800/40 border border-slate-700 rounded-lg p-3">
        <div className="flex items-start gap-3 text-sm text-slate-300">
          <Mail className="w-4 h-4 text-sky-400 mt-0.5" />
          <div>
            <p className="font-medium text-white">Auto-Reminder Sweep</p>
            <p className="text-xs text-slate-400">Approved Super Agents who haven't activated within 48h are re-emailed automatically (max 2 reminders).</p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={async () => {
          try {
            const res = await fetch(`${API_URL}/api/company-onboarding/run-activation-reminders`, { method: 'POST', headers });
            const data = await res.json();
            if (res.ok) toast.success(`Reminder sweep run · sent ${data.sent || 0}, errors ${data.errors || 0}`);
            else toast.error(data.detail || 'Sweep failed');
          } catch { toast.error('Network error'); }
        }} className="border-sky-700 text-sky-300 hover:bg-sky-500/10" data-testid="run-reminder-sweep-btn">
          <Send className="w-4 h-4 mr-2" /> Run Sweep Now
        </Button>
      </div>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2"><Building2 className="w-5 h-5 text-indigo-400" />Management Company Applications</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8 text-slate-400"><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading…</div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-slate-400 py-8">No applications.</p>
          ) : (
            <div className="space-y-2">
              {filtered.map((a) => (
                <div key={a.application_id} className="flex items-center justify-between p-3 bg-slate-700/40 rounded-lg hover:bg-slate-700/60 cursor-pointer transition" onClick={() => openDetail(a)} data-testid={`app-row-${a.application_id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-white">{a.company_name}</p>
                      {statusBadge(a.status)}
                      {a.country ? <Badge className="bg-slate-600 text-slate-200">{a.country}</Badge> : null}
                    </div>
                    <p className="text-sm text-slate-400">{a.primary_contact_name} · {a.primary_contact_email} · {a.buildings_managed || 0} buildings / {a.units_managed || 0} units</p>
                    <p className="text-xs text-slate-500">Submitted {a.created_at?.slice(0, 16).replace('T', ' ')}</p>
                  </div>
                  <div className="text-slate-400 text-xs hidden md:block">{a.application_id}</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setDetail(null); } }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="application-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-400" /> {selected?.company_name}
            </DialogTitle>
            <DialogDescription className="text-slate-400" asChild>
              <div>Application {selected?.application_id} · {selected && statusBadge(selected.status)}</div>
            </DialogDescription>
          </DialogHeader>

          {!detail ? (
            <div className="py-6 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</div>
          ) : (
            <div className="space-y-4 text-sm">
              <Section title="Company">
                <KV k="Email" v={detail.application.company_email} />
                <KV k="Phone" v={detail.application.company_phone} />
                <KV k="Reg #" v={detail.application.registration_number} />
                <KV k="VAT #" v={detail.application.vat_number} />
                <KV k="Address" v={detail.application.physical_address} />
                <KV k="Country" v={detail.application.country} />
              </Section>
              <Section title="Primary Contact (Super Agent)">
                <KV k="Name" v={detail.application.primary_contact_name} />
                <KV k="Position" v={detail.application.primary_contact_position} />
                <KV k="Email" v={detail.application.primary_contact_email} />
                <KV k="Phone" v={detail.application.primary_contact_phone} />
              </Section>
              <Section title="Portfolio">
                <KV k="Buildings" v={detail.application.buildings_managed} />
                <KV k="Units" v={detail.application.units_managed} />
                <KV k="Areas" v={detail.application.areas_of_operation} />
              </Section>
              <Section title="Terms Acceptance">
                <KV k="Accepted" v={detail.application.terms_accepted ? 'Yes' : 'No'} />
                <KV k="At" v={detail.application.terms_acceptance?.accepted_at?.slice(0, 19).replace('T', ' ')} />
                <KV k="IP" v={detail.application.terms_acceptance?.ip} />
              </Section>

              <div className="flex flex-wrap gap-2">
                {detail.application.loi_file_ids?.pdf && (
                  <Button size="sm" variant="outline" className="border-slate-600 text-slate-200 hover:bg-slate-800" onClick={() => downloadLoi(detail.application.loi_file_ids.pdf, `LOI_${detail.application.company_name}.pdf`)} data-testid="download-loi-pdf-btn">
                    <Download className="w-4 h-4 mr-2" /> LOI (PDF)
                  </Button>
                )}
                {detail.application.loi_file_ids?.docx && (
                  <Button size="sm" variant="outline" className="border-slate-600 text-slate-200 hover:bg-slate-800" onClick={() => downloadLoi(detail.application.loi_file_ids.docx, `LOI_${detail.application.company_name}.docx`)} data-testid="download-loi-docx-btn">
                    <FileText className="w-4 h-4 mr-2" /> LOI (DOCX)
                  </Button>
                )}
              </div>

              {detail.audit?.length > 0 && (
                <Section title="Audit Trail">
                  <ul className="space-y-1 text-xs text-slate-400 max-h-40 overflow-y-auto">
                    {detail.audit.map((e) => (
                      <li key={e.log_id}>· {e.created_at?.slice(0, 19).replace('T', ' ')} — <span className="text-slate-200">{e.action}</span> by {e.actor}</li>
                    ))}
                  </ul>
                </Section>
              )}
            </div>
          )}

          <DialogFooter className="flex-wrap gap-2">
            {selected?.status !== 'approved' && selected?.status !== 'rejected' && (
              <>
                <Button onClick={() => setActionType('approve')} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-btn">
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Approve
                </Button>
                <Button variant="outline" onClick={() => setActionType('info')} className="border-amber-600 text-amber-400 hover:bg-amber-500/10" data-testid="request-info-btn">
                  <MessageSquare className="w-4 h-4 mr-2" /> Request Info
                </Button>
                <Button variant="outline" onClick={() => setActionType('reject')} className="border-red-600 text-red-400 hover:bg-red-500/10" data-testid="reject-btn">
                  <XCircle className="w-4 h-4 mr-2" /> Reject
                </Button>
              </>
            )}
            {selected?.status === 'approved' && (
              <Button onClick={() => setActionType('resend')} className="bg-sky-600 hover:bg-sky-700" data-testid="resend-activation-btn">
                <Send className="w-4 h-4 mr-2" /> Resend Activation Email
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!actionType} onOpenChange={(o) => { if (!o) { setActionType(null); setNote(''); } }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white" data-testid="action-confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {actionType === 'approve' && 'Approve Application'}
              {actionType === 'reject' && 'Reject Application'}
              {actionType === 'info' && 'Request More Information'}
              {actionType === 'resend' && 'Resend Activation Email'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {actionType === 'approve' && 'A management company profile will be created and an activation email with OTP + magic link will be sent to the primary contact.'}
              {actionType === 'reject' && 'The applicant will be notified. Please give a reason below.'}
              {actionType === 'info' && 'Tell the applicant exactly what information is missing — they will receive an email with this note.'}
              {actionType === 'resend' && 'A new 8-digit OTP and activation link will be sent to the Super Agent.'}
            </DialogDescription>
          </DialogHeader>
          {(actionType === 'reject' || actionType === 'info' || actionType === 'approve') && (
            <div className="space-y-2">
              <Label>{actionType === 'approve' ? 'Internal note (optional)' : 'Note (required)'}</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} className="bg-slate-800 border-slate-700 text-slate-200" data-testid="action-note-input" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setActionType(null); setNote(''); }} className="border-slate-700">Cancel</Button>
            <Button onClick={submitAction} disabled={busy} className={
              actionType === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' :
              actionType === 'reject' ? 'bg-red-600 hover:bg-red-700' :
              actionType === 'resend' ? 'bg-sky-600 hover:bg-sky-700' :
              'bg-amber-600 hover:bg-amber-700'
            } data-testid="confirm-action-btn">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">{title}</p>
      <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 grid grid-cols-2 gap-x-4 gap-y-1">{children}</div>
    </div>
  );
}
function KV({ k, v }) {
  if (v === undefined || v === null || v === '') return null;
  return (<><span className="text-slate-400">{k}</span><span className="text-slate-100">{String(v)}</span></>);
}
